/* eslint-disable no-use-before-define */
import React, { useRef, forwardRef, useImperativeHandle, useState } from 'react';
import { CircularProgress } from '@material-ui/core';

interface YoutubeIframeProps {
  url: string;
  autoPlay?: number;
  mute?: number;
}

export interface YoutubeIframeRef {
  pauseVideo: () => void;
}

const extractYouTubeVideoId = (url: string): string | null => {
  const regExp = /(?:youtube\.com\/.*v=|youtu\.be\/)([\w-]{11})/;
  const match = url?.match(regExp);
  return match ? match[1] : null;
};

const YoutubeIframe = forwardRef<YoutubeIframeRef, YoutubeIframeProps>(
  ({ url, autoPlay = 0, mute = 0 }, ref) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const videoId = extractYouTubeVideoId(url);

    useImperativeHandle(ref, () => ({
      pauseVideo: () => {
        if (iframeRef.current && iframeRef.current.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
            '*'
          );
        }
      },
    }));

    if (!videoId) {
      return (
        <div
          style={{
            background: 'grey',
            height: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <p style={{ color: 'white', textAlign: 'center' }}>
            URL YouTube invalide ou non reconnue.
          </p>
        </div>
      );
    }

    const embedUrl = `https://www.youtube.com/embed/${videoId}?enablejsapi=1&hd=1&vq=hd720&autoplay=${autoPlay}&mute=${mute}`;

    return (
      <div style={{ position: 'relative', paddingTop: '56.25%' }}>
        {isLoading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0, 0, 0, 0.05)',
              zIndex: 1,
            }}>
            <CircularProgress style={{ color: '#0E1247' }} />
          </div>
        )}
        <iframe
          ref={iframeRef}
          src={embedUrl}
          title="YouTube Video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen={true}
          loading="lazy"
          onLoad={() => setIsLoading(false)}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: 5,
          }}
        />
      </div>
    );
  }
);

YoutubeIframe.displayName = 'YoutubeIframe';

export default YoutubeIframe;
